import { useState, useEffect } from "react";
import Navbar from "../Navbar/index.js";
import Roster from "./roster.js";
import usePartyStore from "../../../stores/party.js";
import useSocketStore from "../../../stores/socket.js";
import { generateQrCode, settings } from "@tumbodash/browserPackages";
import Player from "../../../modules/player/index.js";

function Lobby() {
	const [qrCode, setQrCode] = useState(
		"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAEElEQVR42mP8/5+hHgAHggJ/PWCR/wAAAABJRU5ErkJggg==",
	);

	const socket = useSocketStore.getState().socket!;
	const party = usePartyStore.getState().party!;
	const playerId = Player.getActiveId();
	const player = party.players[playerId!];

	const [nameInput, setNameInput] = useState(player.name);

	function saveName() {
		const trimmed = nameInput.trim();
		if (!trimmed || trimmed === player.name) return;
		socket.emit("player:update", { data: { name: trimmed } });
	}

	const playerReady = () => {
		socket.emit("player:isReady", { emitCode: 34449 });
	};

	const partyUrl = settings.appUrl + "#/p/" + party.id;

	function share() {
		navigator.share({
			title: "TumboDash",
			text: "Play TumboDash with me!",
			url: partyUrl,
		});
	}

	useEffect(() => {
		async function runGenerateQrCode() {
			const code = await generateQrCode(partyUrl);
			setQrCode(code);
		}
		runGenerateQrCode();
	}, [partyUrl]);

	return (
		<div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column", background: "var(--bg-base)" }}>
			<Navbar party={party}>
				{player.isReady ? (
					<div style={{
						display: "flex",
						alignItems: "center",
						gap: "var(--space-2)",
						color: "#4ec9b7",
						fontWeight: 700,
						fontSize: "0.9rem",
					}}>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
							<path d="M20 6L9 17l-5-5" />
						</svg>
						Ready
					</div>
				) : (
					<button
						onClick={playerReady}
						style={{
							padding: "var(--space-2) var(--space-4)",
							borderRadius: "var(--radius-pill)",
							border: "none",
							background: "var(--accent)",
							color: "#fff",
							fontWeight: 700,
							fontSize: "0.88rem",
							fontFamily: "var(--font-ui)",
							cursor: "pointer",
							boxShadow: "0 2px 10px rgba(255,107,107,0.35)",
							letterSpacing: "0.01em",
						}}
					>
						Ready
					</button>
				)}
			</Navbar>

			<div style={{
				flex: 1,
				display: "flex",
				flexDirection: "column",
				gap: "var(--space-5)",
				padding: "var(--space-5) var(--space-4)",
				maxWidth: 480,
				width: "100%",
				margin: "0 auto",
			}}>

				{/* ── Player info ── */}
				<div style={{
					background: "var(--bg-surface)",
					border: "1px solid var(--border)",
					borderRadius: "var(--radius-lg)",
					padding: "var(--space-4)",
					display: "flex",
					flexDirection: "column",
					gap: "var(--space-3)",
				}}>
					{/* Name */}
					<div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
						<span style={{
							fontSize: "0.72rem",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
							color: "var(--text-muted)",
							flexShrink: 0,
						}}>
							Name
						</span>
						<input
							style={{
								flex: 1,
								background: "transparent",
								border: "none",
								borderBottom: "1px solid var(--border-strong)",
								borderRadius: 0,
								color: player.color,
								fontWeight: 700,
								fontSize: "1rem",
								fontFamily: "var(--font-ui)",
								padding: "var(--space-1) 0",
								outline: "none",
							}}
							value={nameInput}
							onChange={e => setNameInput(e.target.value)}
							onBlur={saveName}
							onKeyDown={e => e.key === "Enter" && e.currentTarget.blur()}
							maxLength={20}
						/>
					</div>

					{/* Divider */}
					<div style={{ height: 1, background: "var(--border)" }} />

					{/* Party ID */}
					<div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
						<span style={{
							fontSize: "0.72rem",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
							color: "var(--text-muted)",
							flexShrink: 0,
						}}>
							Party ID
						</span>
						<span style={{
							flex: 1,
							fontWeight: 700,
							fontSize: "1rem",
							color: "var(--text-primary)",
							letterSpacing: "0.05em",
						}}>
							{party.id}
						</span>
						<button
							onClick={share}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "var(--space-1)",
								padding: "var(--space-1) var(--space-3)",
								borderRadius: "var(--radius-pill)",
								border: "1px solid var(--border-strong)",
								background: "transparent",
								color: "var(--text-secondary)",
								fontSize: "0.78rem",
								fontWeight: 600,
								fontFamily: "var(--font-ui)",
								cursor: "pointer",
							}}
						>
							Invite
							<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
								<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
								<polyline points="16 6 12 2 8 6" />
								<line x1="12" y1="2" x2="12" y2="15" />
							</svg>
						</button>
					</div>
				</div>

				{/* ── Roster ── */}
				<div>
					<div style={{
						fontSize: "0.72rem",
						fontWeight: 700,
						letterSpacing: "0.08em",
						textTransform: "uppercase",
						color: "var(--text-muted)",
						marginBottom: "var(--space-3)",
					}}>
						Players
					</div>
					<Roster party={party} socket={socket} />
				</div>

				{/* ── QR Code ── */}
				<div style={{ textAlign: "center" }}>
					<div style={{
						fontSize: "0.72rem",
						fontWeight: 700,
						letterSpacing: "0.08em",
						textTransform: "uppercase",
						color: "var(--text-muted)",
						marginBottom: "var(--space-3)",
					}}>
						Scan to join
					</div>
					<div style={{
						display: "inline-block",
						padding: "var(--space-3)",
						background: "#fff",
						borderRadius: "var(--radius-md)",
					}}>
						<img src={qrCode} alt="QR Code" style={{ display: "block", width: 140, height: 140 }} />
					</div>
				</div>

			</div>
		</div>
	);
}

export default Lobby;
